<template>
  <!-- Already planned: read-only, never written by the split. -->
  <div v-if="card.kind === 'planned'" class="grid grid-cols-[170px_1fr_auto] items-center gap-3.5 rounded-[10px] bg-gray-50 px-4 py-2.5 text-[13px] text-gray-500">
    <b class="font-semibold">{{ card.week.label }}</b>
    <span class="text-xs">
      Already planned here ·
      <span class="font-semibold tabular-nums text-gray-700">{{ card.plannedSummary }}</span>
      ·
      <button class="text-xs font-medium text-blue-700 hover:underline" type="button" @click="$emit('showOnBoard', card.week.weekKey)">Show on board</button>
    </span>
    <span class="chip chip--skip">read-only</span>
  </div>

  <!-- An active week, and a skipped one: the same card. A skipped week simply has no active
       line, so every line offers "+ Add capacity" where its bar would be. -->
  <div
    v-else
    class="flex flex-col gap-[7px] rounded-[10px] border bg-white px-4 pb-3 pt-2.5"
    :class="[
      card.overTypes.length > 0 || card.saveState === 'failed' ? 'border-red-200' : 'border-gray-200',
      card.isFirst ? 'bg-blue-50' : '',
      card.saveState === 'waiting' ? 'opacity-70' : '',
    ]"
  >
    <div class="flex items-center justify-between gap-3.5">
      <span class="inline-flex items-baseline gap-2">
        <b class="inline-flex items-center gap-1.5 text-[13px] font-bold text-gray-900">
          <i class="inline-block h-2 w-2 rounded-sm" :style="{ backgroundColor: swatch }" aria-hidden="true" />
          {{ card.week.label }}
        </b>
        <small class="text-[11px]" :class="card.tag.includes('changed') ? 'text-blue-700' : 'text-gray-400'">{{ card.tag }}</small>
      </span>
      <!-- A skipped week holds nothing, so there is no state to chip; without write:capacity
           it keeps the chip its one-line row used to carry, as there is no action to offer. -->
      <template v-if="card.kind === 'skipped'">
        <span v-if="!canAddPlan" class="chip chip--skip">skipped</span>
      </template>
      <!-- One chip per card: save state while saving, else fits / over. -->
      <span v-else-if="card.saveState === 'saved'" class="chip chip--ok">✓ saved</span>
      <span v-else-if="card.saveState === 'saving'" class="chip chip--busy">
        <span class="spinner" aria-hidden="true" />
        saving…
      </span>
      <span v-else-if="card.saveState === 'failed'" class="chip chip--bad">
        ✕ failed ·
        <button class="underline" type="button" @click="$emit('retry')">Retry</button>
      </span>
      <span v-else-if="card.saveState === 'waiting'" class="chip chip--skip">waiting</span>
      <span v-else-if="card.overTypes.length > 0" class="chip chip--bad">{{ overText }}</span>
      <span v-else class="chip chip--ok">✓ fits</span>
    </div>

    <div v-for="row in rows" :key="row.line.type.capacityTypeId" class="grid grid-cols-[170px_1fr_96px] items-center gap-3.5">
      <!-- Pill input: the type code is part of the control, so "LC 8,000" reads as one fact. -->
      <label class="pill" :class="[row.line.over ? 'pill--over' : row.line.edited ? 'pill--edited' : '', row.line.active ? '' : 'pill--off']">
        <span class="pill__cap">{{ row.line.type.code }}</span>
        <input
          class="pill__input tabular-nums"
          type="number"
          min="0"
          :value="row.line.value"
          :disabled="!row.line.active || disabled"
          :aria-label="`${row.line.type.code} in ${card.week.label}`"
          @change="onChange(row.line.type.capacityTypeId, $event)"
        />
      </label>

      <!-- Bar column: the live bar, or — where a plan could be created — the action that
           creates it, so the button sits exactly where the bar would start. -->
      <span v-if="row.addPlan">
        <SplitAddPlanButton
          :menu="menu"
          :row-id="row.addPlan.rowId"
          :week-key="card.week.weekKey"
          :week-label="card.week.label"
          :code="row.line.type.code"
          :unit="row.addPlan.unit"
          :disabled="disabled"
        />
      </span>
      <span v-else-if="!row.line.active" class="h-2.5 rounded border border-dashed border-gray-200 bg-gray-50" aria-hidden="true" />
      <SplitCapacityBar v-else :week="card.week" :type="row.line.type" :value="row.line.value" />

      <span v-if="row.line.active" class="text-right text-[10px] tabular-nums" :class="row.line.over ? 'font-semibold text-red-600' : 'text-gray-500'">
        {{ leftText(row.line) }}
      </span>
      <span v-else-if="!row.addPlan" class="text-right text-[10px] tabular-nums text-gray-400">{{ row.line.reason }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick } from 'vue';

  import SplitAddPlanButton from '@/components/scheduling/SplitAddPlanButton.vue';
  import SplitCapacityBar from '@/components/scheduling/SplitCapacityBar.vue';
  import type { NoPlanMenu } from '@/composables/useNoPlanMenu';
  import type { WeekCardLine, WeekCardModel } from '@/composables/useSplitAcrossWeeks';

  const props = defineProps<{
    card: WeekCardModel;
    swatch: string;
    disabled: boolean;
    // write:capacity. Without it the card offers no create-a-plan action at all.
    canAddPlan: boolean;
    menu: NoPlanMenu;
  }>();
  const emit = defineEmits<{ change: [capacityTypeId: string, value: number]; retry: []; showOnBoard: [weekKey: string] }>();

  // A line can offer the action only where creating a plan would make it live: the order
  // carries the type, the week has no plan for it, and the user may write capacity.
  const rows = computed(() =>
    props.card.lines.map((line) => ({
      line,
      addPlan: props.canAddPlan && line.rowId !== null ? { rowId: line.rowId, unit: line.unit } : null,
    }))
  );

  const overText = computed(() =>
    props.card.lines
      .filter((line) => line.over)
      .map((line) => `${line.type.code} ${Math.abs(line.left ?? 0).toLocaleString()} over`)
      .join(' · ')
  );

  // `over` (red) only once a quantity is placed pushes it past capacity (F5 rule); an
  // already-overbooked week with nothing placed here reads as "short" rather than a
  // "-50 left" that looks like a typo.
  function leftText(line: WeekCardLine): string {
    const left = line.left ?? 0;
    if (line.over) return `${Math.abs(left).toLocaleString()} over`;
    if (left < 0) return `${Math.abs(left).toLocaleString()} short`;
    return `${left.toLocaleString()} left`;
  }

  // The input is uncontrolled between changes, so a value the composable clamps (or refuses
  // while locked) would stay on screen as typed. Write the model back once it has settled.
  async function onChange(capacityTypeId: string, event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    emit('change', capacityTypeId, Number(input.value));
    await nextTick();
    input.value = String(props.card.lines.find((line) => line.type.capacityTypeId === capacityTypeId)?.value ?? 0);
  }
</script>

<style scoped>
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
  }
  .chip--ok {
    background: #f0fdf4;
    color: #16a34a;
  }
  .chip--bad {
    background: #fef2f2;
    color: #dc2626;
  }
  .chip--skip {
    background: #f3f4f6;
    color: #6b7280;
  }
  .chip--busy {
    background: #eff6ff;
    color: #1d4ed8;
  }
  .spinner {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .spinner {
      animation: none;
    }
  }
  .pill {
    display: inline-flex;
    align-items: center;
    overflow: hidden;
    border: 1px solid rgb(203 213 225);
    border-radius: 8px;
    background: #fff;
  }
  .pill--edited {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgb(37 99 235 / 15%);
  }
  .pill--over {
    border-color: #dc2626;
    box-shadow: 0 0 0 2px rgb(220 38 38 / 15%);
  }
  .pill--off {
    border-color: #e5e7eb;
    background: #f9fafb;
  }
  .pill__cap {
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 700;
    color: #6b7280;
    background: #f9fafb;
    border-right: 1px solid #e5e7eb;
  }
  .pill--off .pill__cap {
    color: #9ca3af;
    background: transparent;
  }
  .pill__input {
    width: 86px;
    border: 0;
    padding: 4px 10px;
    font-size: 13px;
    text-align: right;
    background: transparent;
    color: #111827;
  }
  .pill__input:disabled {
    color: #9ca3af;
  }
  .pill__input:focus {
    outline: none;
  }
  .pill:focus-within {
    outline: 2px solid #2563eb;
    outline-offset: 1px;
  }
</style>
